// src/pages/Hr/WorkPatternsPage.jsx
// =============================================================================
// HR -> Work Patterns
// =============================================================================
//
// PURPOSE
// -------
// This page maintains reusable rotating work-pattern templates for Leave / HR.
//
// Why this exists:
// - HB has employees whose real working week can rotate, for example:
//   week A = Monday-Friday, week B = Tuesday-Saturday.
// - Maintaining exact day-by-day work schedules for every employee would create
//   too much administrative effort.
// - A reusable pattern plus a cycle start date allows the system to calculate
//   leave deductions accurately without forcing manual schedule maintenance.
//
// Access model:
// - every authenticated user may view available patterns,
// - only superusers may maintain the templates.
//
// The actual assignment of a pattern to an employee is handled in User Settings
// / Vacation Settings, because it is employee-specific master data.

import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import AuthContext from "../../context/AuthContext";
import {
  createWorkPattern,
  listWorkPatterns,
  setWorkPatternActive,
  updateWorkPattern,
} from "../../services/hrCalendarService";

import "./HrMasterDataPage.css";

const DAY_NUMBERS = [1, 2, 3, 4, 5, 6, 7];

const EMPTY_FORM = {
  id: null,
  code: "",
  name: "",
  description: "",
  cycle_length_weeks: 1,
  sort_order: 0,
  is_active: true,
  days: [],
};

function normalizeRole(role) {
  return String(role || "").trim().toLowerCase();
}

function getErrorMessage(err, fallback) {
  const backend =
    err?.data?.message ||
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    "";

  if (backend) return String(backend);

  const plain = String(err?.message || "").trim();
  return plain || fallback;
}

function normalizePattern(row) {
  const cycle = Number(row?.cycle_length_weeks || 1);
  return {
    ...EMPTY_FORM,
    ...(row || {}),
    id: row?.id ?? null,
    code: row?.code || "",
    name: row?.name || "",
    description: row?.description || "",
    cycle_length_weeks: Number.isInteger(cycle) && cycle > 0 ? cycle : 1,
    sort_order: Number(row?.sort_order || 0),
    is_active: row?.is_active !== false,
    days: Array.isArray(row?.days) ? row.days.map(normalizeDay) : [],
  };
}

function normalizeDay(row) {
  return {
    week_index: Number(row?.week_index || 1),
    day_of_week: Number(row?.day_of_week || 1),
    minutes: Number(row?.minutes || 0),
  };
}

function buildBlankDays(cycleLength) {
  const weeks = Number(cycleLength || 1);
  const rows = [];

  for (let week = 1; week <= weeks; week += 1) {
    for (const day of DAY_NUMBERS) {
      rows.push({ week_index: week, day_of_week: day, minutes: 0 });
    }
  }

  return rows;
}

function mergeDaysWithCycle(existingDays, cycleLength) {
  const map = new Map();
  for (const row of existingDays || []) {
    map.set(`${row.week_index}:${row.day_of_week}`, normalizeDay(row));
  }

  return buildBlankDays(cycleLength).map((row) => {
    const existing = map.get(`${row.week_index}:${row.day_of_week}`);
    return existing || row;
  });
}

function getDayLabel(t, day) {
  const keys = {
    1: "mon",
    2: "tue",
    3: "wed",
    4: "thu",
    5: "fri",
    6: "sat",
    7: "sun",
  };

  return t(`leaves:vacationSettings.days.${keys[day]}`);
}

function applyMonFriPreset(cycleLength) {
  return buildBlankDays(cycleLength).map((row) => ({
    ...row,
    minutes: row.day_of_week >= 1 && row.day_of_week <= 5 ? 480 : 0,
  }));
}

function applyRotatingHbPreset() {
  return buildBlankDays(2).map((row) => {
    if (row.week_index === 1) {
      return {
        ...row,
        minutes: row.day_of_week >= 1 && row.day_of_week <= 5 ? 480 : 0,
      };
    }

    // Week 2: Tuesday-Saturday.  Friday/Saturday use the concrete HB example
    // discussed for rotating work: Friday 9h and Saturday 7h, but the user can
    // still overwrite any cell before saving.
    if (row.day_of_week >= 2 && row.day_of_week <= 4) return { ...row, minutes: 480 };
    if (row.day_of_week === 5) return { ...row, minutes: 540 };
    if (row.day_of_week === 6) return { ...row, minutes: 420 };
    return { ...row, minutes: 0 };
  });
}

function patternWeeklyTotal(days, weekIndex) {
  return (days || [])
    .filter((row) => Number(row.week_index) === Number(weekIndex))
    .reduce((sum, row) => sum + Number(row.minutes || 0), 0);
}

export default function WorkPatternsPage() {
  const { t } = useTranslation(["common", "leaves", "navigation"]);
  const { authState } = useContext(AuthContext);

  const isSuperUser = normalizeRole(authState?.user?.role) === "superuser";

  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ ...EMPTY_FORM, days: buildBlankDays(1) });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(true);

  const resetForm = useCallback(() => {
    setForm({ ...EMPTY_FORM, days: buildBlankDays(1) });
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await listWorkPatterns({ includeInactive: includeInactive ? "1" : "0" });
      setRows(Array.isArray(data) ? data.map(normalizePattern) : []);
    } catch (err) {
      setError(getErrorMessage(err, t("leaves:workPatterns.loadError")));
    } finally {
      setLoading(false);
    }
  }, [includeInactive, t]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((row) => {
      const haystack = [row.code, row.name, row.description]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, search]);

  const updateField = (name, value) => {
    setForm((prev) => {
      if (name === "cycle_length_weeks") {
        const cycle = Math.max(1, Math.min(12, Number(value || 1)));
        return {
          ...prev,
          cycle_length_weeks: cycle,
          days: mergeDaysWithCycle(prev.days, cycle),
        };
      }

      return { ...prev, [name]: value };
    });

    setSuccess("");
    setError("");
  };

  const updateMinutes = (weekIndex, dayOfWeek, value) => {
    const minutes = Number(value || 0);
    setForm((prev) => ({
      ...prev,
      days: mergeDaysWithCycle(prev.days, prev.cycle_length_weeks).map((row) => {
        if (Number(row.week_index) === Number(weekIndex) && Number(row.day_of_week) === Number(dayOfWeek)) {
          return { ...row, minutes };
        }
        return row;
      }),
    }));
  };

  const selectForEdit = (row) => {
    const normalized = normalizePattern(row);
    setForm({
      ...normalized,
      days: mergeDaysWithCycle(normalized.days, normalized.cycle_length_weeks),
    });
    setSuccess("");
    setError("");
  };

  const validateForm = () => {
    if (!String(form.code || "").trim()) return t("leaves:workPatterns.validation.codeRequired");
    if (!String(form.name || "").trim()) return t("leaves:workPatterns.validation.nameRequired");

    const cycle = Number(form.cycle_length_weeks);
    if (!Number.isInteger(cycle) || cycle < 1 || cycle > 12) {
      return t("leaves:workPatterns.validation.cycleRange");
    }

    for (const day of mergeDaysWithCycle(form.days, cycle)) {
      const minutes = Number(day.minutes);
      if (!Number.isInteger(minutes) || minutes < 0 || minutes > 1440) {
        return t("leaves:workPatterns.validation.minutesRange");
      }
    }

    return "";
  };

  const buildPayload = () => ({
    code: form.code,
    name: String(form.name || "").trim(),
    description: String(form.description || "").trim() || null,
    cycle_length_weeks: Number(form.cycle_length_weeks || 1),
    sort_order: Number(form.sort_order || 0),
    is_active: Boolean(form.is_active),
    days: mergeDaysWithCycle(form.days, form.cycle_length_weeks),
  });

  const handleSave = async (event) => {
    event.preventDefault();

    if (!isSuperUser) return;

    const validation = validateForm();
    if (validation) {
      setError(validation);
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      if (form.id) {
        await updateWorkPattern(form.id, buildPayload());
        setSuccess(t("leaves:workPatterns.updated"));
      } else {
        await createWorkPattern(buildPayload());
        setSuccess(t("leaves:workPatterns.created"));
      }

      resetForm();
      await loadRows();
    } catch (err) {
      setError(getErrorMessage(err, t("leaves:workPatterns.saveError")));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (row) => {
    if (!isSuperUser || !row?.id) return;

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const next = !row.is_active;
      await setWorkPatternActive(row.id, next);
      setSuccess(
        next ? t("leaves:workPatterns.activated") : t("leaves:workPatterns.deactivated")
      );
      await loadRows();
    } catch (err) {
      setError(getErrorMessage(err, t("leaves:workPatterns.toggleError")));
    } finally {
      setSaving(false);
    }
  };

  const cycleWeeks = useMemo(() => {
    const cycle = Number(form.cycle_length_weeks || 1);
    return Array.from({ length: cycle }, (_, idx) => idx + 1);
  }, [form.cycle_length_weeks]);

  const formDays = useMemo(
    () => mergeDaysWithCycle(form.days, form.cycle_length_weeks),
    [form.days, form.cycle_length_weeks]
  );

  return (
    <div className="hr-md-page">
      <header className="hr-md-header">
        <div>
          <p className="hr-md-eyebrow">{t("navigation:hr")}</p>
          <h1 className="hr-md-title">{t("leaves:workPatterns.title")}</h1>
          <p className="hr-md-subtitle">{t("leaves:workPatterns.subtitle")}</p>
        </div>
      </header>

      <div className="hr-md-info-box">{t("leaves:workPatterns.info")}</div>

      {error && <div className="hr-md-error">{error}</div>}
      {success && <div className="hr-md-success">{success}</div>}

      <div className="hr-md-grid">
        <section className="hr-md-card">
          <h2 className="hr-md-card-title">
            {form.id ? t("leaves:workPatterns.editTitle") : t("leaves:workPatterns.createTitle")}
          </h2>
          <p className="hr-md-card-description">
            {isSuperUser ? t("leaves:workPatterns.formHelp") : t("leaves:workPatterns.readOnlyHelp")}
          </p>

          <form onSubmit={handleSave}>
            <div className="hr-md-form-grid">
              <label className="hr-md-form-field">
                <span className="hr-md-label">{t("leaves:workPatterns.code")}</span>
                <input
                  className="hr-md-input"
                  value={form.code}
                  disabled={!isSuperUser || saving}
                  onChange={(e) => updateField("code", e.target.value)}
                  placeholder="FULL_TIME_ROTATING_2W"
                />
              </label>

              <label className="hr-md-form-field">
                <span className="hr-md-label">{t("leaves:workPatterns.cycleLength")}</span>
                <input
                  className="hr-md-input"
                  type="number"
                  min="1"
                  max="12"
                  step="1"
                  value={form.cycle_length_weeks}
                  disabled={!isSuperUser || saving}
                  onChange={(e) => updateField("cycle_length_weeks", e.target.value)}
                />
              </label>

              <label className="hr-md-form-field hr-md-form-field--full">
                <span className="hr-md-label">{t("leaves:workPatterns.name")}</span>
                <input
                  className="hr-md-input"
                  value={form.name}
                  disabled={!isSuperUser || saving}
                  onChange={(e) => updateField("name", e.target.value)}
                  placeholder={t("leaves:workPatterns.namePlaceholder")}
                />
              </label>

              <label className="hr-md-form-field">
                <span className="hr-md-label">{t("leaves:workPatterns.sortOrder")}</span>
                <input
                  className="hr-md-input"
                  type="number"
                  step="1"
                  value={form.sort_order}
                  disabled={!isSuperUser || saving}
                  onChange={(e) => updateField("sort_order", e.target.value)}
                />
              </label>

              <label className="hr-md-checkbox-row">
                <input
                  type="checkbox"
                  checked={Boolean(form.is_active)}
                  disabled={!isSuperUser || saving}
                  onChange={(e) => updateField("is_active", e.target.checked)}
                />
                {t("common:status.active")}
              </label>

              <label className="hr-md-form-field hr-md-form-field--full">
                <span className="hr-md-label">{t("leaves:workPatterns.description")}</span>
                <textarea
                  className="hr-md-textarea"
                  value={form.description}
                  disabled={!isSuperUser || saving}
                  onChange={(e) => updateField("description", e.target.value)}
                  placeholder={t("leaves:workPatterns.descriptionPlaceholder")}
                />
              </label>
            </div>

            {isSuperUser && (
              <div className="hr-md-actions">
                <button
                  type="button"
                  className="hr-md-button"
                  disabled={saving}
                  onClick={() => setForm((prev) => ({ ...prev, days: applyMonFriPreset(prev.cycle_length_weeks) }))}
                >
                  {t("leaves:workPatterns.presetMonFri")}
                </button>
                <button
                  type="button"
                  className="hr-md-button"
                  disabled={saving}
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      cycle_length_weeks: 2,
                      days: applyRotatingHbPreset(),
                    }))
                  }
                >
                  {t("leaves:workPatterns.presetRotating")}
                </button>
              </div>
            )}

            <div className="hr-md-pattern-grid">
              {cycleWeeks.map((week) => (
                <div className="hr-md-week-card" key={week}>
                  <div className="hr-md-week-title">
                    {t("leaves:workPatterns.week", { week })}
                  </div>
                  {DAY_NUMBERS.map((day) => {
                    const cell = formDays.find(
                      (row) => row.week_index === week && row.day_of_week === day
                    );
                    return (
                      <label className="hr-md-form-field" key={`${week}:${day}`}>
                        <span className="hr-md-label">{getDayLabel(t, day)}</span>
                        <input
                          className="hr-md-input"
                          type="number"
                          min="0"
                          max="1440"
                          step="1"
                          value={cell?.minutes ?? 0}
                          disabled={!isSuperUser || saving}
                          onChange={(e) => updateMinutes(week, day, e.target.value)}
                        />
                      </label>
                    );
                  })}
                  <div className="hr-md-hint">
                    {t("leaves:workPatterns.weeklyTotal", {
                      minutes: patternWeeklyTotal(formDays, week),
                      hours: (patternWeeklyTotal(formDays, week) / 60).toFixed(2),
                    })}
                  </div>
                </div>
              ))}
            </div>

            {isSuperUser && (
              <div className="hr-md-actions">
                <button type="button" className="hr-md-button" disabled={saving} onClick={resetForm}>
                  {t("common:actions.cancel")}
                </button>
                <button type="submit" className="hr-md-button hr-md-button--primary" disabled={saving}>
                  {saving ? t("common:actions.save") : t("common:actions.save")}
                </button>
              </div>
            )}
          </form>
        </section>

        <section className="hr-md-card">
          <div className="hr-md-toolbar">
            <input
              className="hr-md-input hr-md-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("leaves:workPatterns.searchPlaceholder")}
            />
            <label className="hr-md-checkbox-row">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              {t("leaves:workPatterns.includeInactive")}
            </label>
          </div>

          <div className="hr-md-table-wrap">
            <table className="hr-md-table">
              <thead>
                <tr>
                  <th>{t("leaves:workPatterns.headers.code")}</th>
                  <th>{t("leaves:workPatterns.headers.name")}</th>
                  <th>{t("leaves:workPatterns.headers.cycle")}</th>
                  <th>{t("leaves:workPatterns.headers.status")}</th>
                  <th>{t("leaves:workPatterns.headers.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="5">{t("common:common.loading")}</td>
                  </tr>
                )}

                {!loading && filteredRows.length === 0 && (
                  <tr>
                    <td colSpan="5">{t("leaves:workPatterns.empty")}</td>
                  </tr>
                )}

                {!loading &&
                  filteredRows.map((row) => (
                    <tr key={row.id || row.code}>
                      <td>
                        <div className="hr-md-row-title">{row.code}</div>
                        {row.description && (
                          <div className="hr-md-row-subtitle">{row.description}</div>
                        )}
                      </td>
                      <td>{row.name}</td>
                      <td>{t("leaves:workPatterns.weeksCount", { count: row.cycle_length_weeks })}</td>
                      <td>
                        <span className={row.is_active ? "hr-md-badge" : "hr-md-badge hr-md-badge--inactive"}>
                          {row.is_active ? t("common:status.active") : t("common:status.inactive")}
                        </span>
                      </td>
                      <td>
                        <button type="button" className="hr-md-button" onClick={() => selectForEdit(row)}>
                          {isSuperUser ? t("common:actions.edit") : t("common:actions.open")}
                        </button>
                        {isSuperUser && (
                          <button
                            type="button"
                            className="hr-md-button hr-md-button--dangerish"
                            disabled={saving}
                            onClick={() => handleToggleActive(row)}
                          >
                            {row.is_active
                              ? t("leaves:workPatterns.deactivate")
                              : t("leaves:workPatterns.activate")}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
